import { gql } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import Table from 'components/Table';
import { FormattedMessage } from 'react-intl';
import Modal from 'components/Modal';
import Page from 'components/Page';
import useCustomer from 'hooks/useCustomer';
import useUser from 'hooks/useUser';
import { Upload, Button, Image, Input } from 'antd';
import { DownloadOutlined, EyeFilled, FileImageOutlined, FileZipOutlined, FilePdfOutlined, FilePptOutlined, FileWordOutlined, FileUnknownOutlined, EditOutlined, PlayCircleOutlined, FileExcelOutlined, UploadOutlined, FolderOutlined, DeleteFilled, CopyOutlined } from '@ant-design/icons';
import byteSize from 'byte-size';
import { SUPER_USER } from 'shared/constants/permissions';
import globalMessages from 'translations/messages';
import useGlobalContext from 'hooks/useGlobalContext';
import EditFile from './EditFile';

const { Dragger } = Upload;

function isVideo(format) {
  return format?.includes('video');
}

function isImage(format) {
  return format?.includes('image');
}

function isZip(format) {
  return format?.includes('application/x-zip-compressed');
}

function isPDF(format) {
  return format?.includes('application/pdf');
}

function isWord(format) {
  return format?.includes('application/msword') || format?.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
}

function isExcel(format) {
  return format?.includes('application/vnd.ms-excel') || format?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

function isPPT(format) {
  return format?.includes('application/vnd.ms-powerpoint');
}

function getIcon({ children, format }) {
  if (children?.length) {
    return <FolderOutlined />;
  }
  if (isVideo(format)) {
    return <PlayCircleOutlined />;
  }
  if (isImage(format)) {
    return <FileImageOutlined />;
  }
  if (isZip(format)) {
    return <FileZipOutlined />;
  }
  if (isPDF(format)) {
    return <FilePdfOutlined />;
  }
  if (isWord(format)) {
    return <FileWordOutlined />;
  }
  if (isExcel(format)) {
    return <FileExcelOutlined />;
  }
  if (isPPT(format)) {
    return <FilePptOutlined />;
  }
  return <FileUnknownOutlined />;
}

const QUERY = gql`
  query Files($customerId: ID) {
    files (customerId: $customerId) {
      id
      createdAt
      title
      format
      size
      s3Key
      url
      downloadUrl
      folder
    }
  }
`;

const UPLOAD_MUTATION = gql`
  mutation($customerId: ID, $file: Upload!, $size: Int!) {
    uploadFile(customerId: $customerId, file: $file, size: $size) { id }
  }
`;

const DELETE_MUTATION = gql`
  mutation DeleteFile($id: ID!) {
    deleteFile(id: $id) {      
      id
    }
  }
`;

const columns = [
  {
    dataIndex: 'title',
    title: 'Name',
    render: (title, record) => (
      <>
        <span style={{ marginRight: 8 }}>{getIcon(record)}</span>
        {title}
      </>
    ),
  },
  {
    dataIndex: 'createdAt',
    title: 'Uploaded',
    type: 'datetime',
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'size',
    title: 'Size',
    sorter: (a, b) => a.size - b.size,
    render: (size) => size && byteSize(size)?.toString(),
  },
];

function getDataSource(data) {
  const files = {};
  const dataSource = [];

  function getFolder(path) {
    if (files[path]) {
      return files[path];
    }

    const folders = path.split('/');

    let parent;

    if (folders.length > 1) {
      parent = getFolder(folders.slice(0, folders.length - 1).join('/'));
    }

    const folder = {
      id: path,
      title: folders[folders.length - 1],
      children: [],
      parent,
    };

    files[path] = folder;

    if (parent) {
      parent.children.push(folder);
    } else {
      dataSource.push(folder);
    }

    return folder;
  }

  if (data?.files) {
    data.files.forEach((file) => {
      if (file.folder?.length) {
        const folder = getFolder(file.folder);
        folder.children.push(file);
      } else {
        dataSource.push(file);
      }
    });
  }

  return dataSource;
}

function getFolderTree(files) {
  return files.filter(({ children }) => children?.length).map(f => ({
    nodeTitle: f.id,
    title: f.title,
    value: f.id,
    children: getFolderTree(f.children),
  }));
}

const Files = ({ title, global }) => {
  const { hasPermission } = useUser();
  const { customer } = useCustomer();
  const customerId = global ? undefined : customer?.id;
  const { data, loading } = useQuery(QUERY, { variables: { customerId } });
  const [deleteFileMutation] = useMutation(DELETE_MUTATION, { refetchQueries: ['Files'] });
  const [editFile, setEditFile] = useState(null);
  const [playVideo, setPlayVideo] = useState(null);
  const [showImage, setShowImage] = useState(null);
  const [mutate] = useMutation(UPLOAD_MUTATION, { refetchQueries: ['Files'] });
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState();
  const { modal, message } = useGlobalContext();

  const upload = {
    multiple: true,
    customRequest: async ({ file, onSuccess, onError, onProgress }) => {
      try {
        await mutate({
          variables: { customerId, file, size: file.size },
          context: {
            fetchOptions: {
              useUpload: true,
              onProgress: (e) => {
                onProgress({ percent: (e.loaded / e.total) * 100 });
              },
            },
          },
        });

        onSuccess();
      } catch (e) {
        message.error(`Something went wrong. ${e.message}`);
        onError(e);
      }
    },
  };

  const onDelete = async (file) => {
    modal.confirm({
      title: 'Remove file',
      content: `Are you sure you want to delete ${file.title}?`,
      okText: 'Remove',
      okType: 'danger',
      async onOk() {
        await deleteFileMutation({
          variables: {
            id: file.id,
          },
        });
      },
    });
  };

  const actions = (record) => !record.children?.length && (
    <>
      {isVideo(record.format) && <Button icon={<EyeFilled />} onClick={() => setPlayVideo(record)}>View</Button>}
      {isImage(record.format) && <Button icon={<EyeFilled />} onClick={() => setShowImage(record)}>View</Button>}
      {!isVideo(record.format) && !isImage(record.format) && <Button icon={<DownloadOutlined />} href={record.downloadUrl}>Download</Button>}
    </>
  );

  const actionsMenuItems = (record => (!record.children?.length ? [{
    key: 'download',
    icon: <DownloadOutlined />,
    label: <a href={record.url}>Download</a>,
    visible: isVideo(record.format) || isImage(record.format),
  }, {
    key: 'copyUrl',
    icon: <CopyOutlined />,
    label: 'Copy URL',
    onClick: () => navigator.clipboard.writeText(record.url),
  }, {
    key: 'edit',
    icon: <EditOutlined />,
    label: 'Edit',
    onClick: () => setEditFile(record),
    visible: hasPermission(SUPER_USER),
  }, {
    key: 'delete',
    icon: <DeleteFilled />,
    label: 'Delete',
    danger: true,
    onClick: () => onDelete(record),
    visible: hasPermission(SUPER_USER),
  }] : null));

  const dataSource = useMemo(() => getDataSource(data), [data]);
  const folders = useMemo(() => getFolderTree(dataSource), [dataSource]);

  return (
    <Page>
      <Page.Header
        title={title || <FormattedMessage {...globalMessages.files} />}
        extra={(
          <>
            <Input.Search onSearch={setSearch} allowClear />

            {hasPermission(SUPER_USER) && (
            <Button type="primary" onClick={() => setShowUpload(true)} icon={<UploadOutlined />}>
              Upload
            </Button>
            )}
          </>
        )}
      />
      <Page.Content>
        <Table
          loading={loading}
          columns={columns}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
          dataSource={dataSource}
          expandRowByClick
          search={search}
        />
        <EditFile file={editFile} onCancel={() => setEditFile(null)} folders={folders} />
        <Modal title={playVideo?.title} open={playVideo} onCancel={() => setPlayVideo(null)} width="600" destroyOnClose footer={null}>
          {playVideo && (
          <video controls autoPlay style={{ width: '100%' }}>
            <source src={playVideo.url} type={playVideo.format} />
          </video>
          )}
        </Modal>
        <Image
          width={200}
          style={{ display: 'none' }}
          preview={{
            visible: showImage,
            src: showImage?.url,
            onVisibleChange: value => {
              setShowImage(value);
            },
          }}
        />
      </Page.Content>
      <Modal open={showUpload} onCancel={() => setShowUpload(false)} footer={false}>
        <Dragger {...upload}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag a file to this area to upload</p>
        </Dragger>
      </Modal>
    </Page>
  );
};

export default Files;
